/**
 * Pure Ops Lookup join/filter helpers (OL-1).
 * See docs/planning/ops_lookup_mode.md.
 */

export type OpsLookupSlider = "sales" | "orders" | "service";

export type OpsLookupLocationKind = "plant" | "dealer" | "customer";

export type OpsLookupSaleRow = {
  id: string;
  display_name: string | null;
  status: string;
  completeness?: number | null;
  paperwork_missing?: number;
};

export type OpsLookupUnitRow = {
  id: string;
  serial: string | null;
  display_label: string;
  sale_path: string;
  plant_id: string;
  ops_deal_id: string | null;
  inventory_holder_org_id: string | null;
  end_customer_contact_id: string | null;
  current_phase_id: string | null;
  staged_at: string | null;
  delivered_at: string | null;
  notes: string;
  customer_name?: string | null;
  seller_location_name?: string | null;
  deal_display_name?: string | null;
};

export type OpsLookupJoined = {
  sale: OpsLookupSaleRow | null;
  unit: OpsLookupUnitRow | null;
};

export type OpsLookupServiceKind = "event" | "request";

export type OpsLookupServiceRow = {
  id: string;
  kind: OpsLookupServiceKind;
  title: string;
  status: string;
  site: string;
  mfg_unit_id: string | null;
  request_id: string | null;
  scheduled_date: string | null;
  quoted_amount_cents: number | null;
  description: string;
};

export function primaryOrderLabel(unit: OpsLookupUnitRow): string {
  const serial = (unit.serial ?? "").trim();
  if (serial) return serial;
  return unit.display_label || unit.id;
}

/** Heuristic until OL-3 wires delivery/holder truth. */
export function inferCurrentLocation(unit: OpsLookupUnitRow): OpsLookupLocationKind {
  if (unit.delivered_at) return "customer";
  if (unit.staged_at || unit.inventory_holder_org_id) return "dealer";
  return "plant";
}

export function matchesOpsLookupQuery(
  query: string,
  parts: Array<string | null | undefined>,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return parts.some((p) => (p ?? "").toLowerCase().includes(needle));
}

export function filterSalesRows(
  sales: OpsLookupSaleRow[],
  unitsByDealId: Map<string, OpsLookupUnitRow>,
  query: string,
): OpsLookupSaleRow[] {
  return sales.filter((s) => {
    const u = unitsByDealId.get(s.id);
    return matchesOpsLookupQuery(query, [
      s.id,
      s.display_name,
      s.status,
      u?.id,
      u?.serial,
      u?.display_label,
      u?.customer_name,
      u?.deal_display_name,
      u?.seller_location_name,
    ]);
  });
}

export function filterOrderRows(units: OpsLookupUnitRow[], query: string): OpsLookupUnitRow[] {
  return units.filter((u) =>
    matchesOpsLookupQuery(query, [
      u.id,
      u.serial,
      u.display_label,
      u.sale_path,
      u.ops_deal_id,
      u.plant_id,
      u.customer_name,
      u.deal_display_name,
      u.seller_location_name,
      u.notes,
    ]),
  );
}

export function indexUnitsByDealId(units: OpsLookupUnitRow[]): Map<string, OpsLookupUnitRow> {
  const map = new Map<string, OpsLookupUnitRow>();
  for (const u of units) {
    if (!u.ops_deal_id) continue;
    // 1:1 for now — first wins if duplicates appear
    if (!map.has(u.ops_deal_id)) map.set(u.ops_deal_id, u);
  }
  return map;
}

export function indexUnitsById(units: OpsLookupUnitRow[]): Map<string, OpsLookupUnitRow> {
  const map = new Map<string, OpsLookupUnitRow>();
  for (const u of units) map.set(u.id, u);
  return map;
}

export function filterServiceRows(
  rows: OpsLookupServiceRow[],
  unitsById: Map<string, OpsLookupUnitRow>,
  query: string,
): OpsLookupServiceRow[] {
  return rows.filter((r) => {
    const u = r.mfg_unit_id ? unitsById.get(r.mfg_unit_id) : undefined;
    return matchesOpsLookupQuery(query, [
      r.id,
      r.title,
      r.status,
      r.site,
      r.description,
      r.kind,
      r.mfg_unit_id,
      r.request_id,
      r.scheduled_date,
      u?.serial,
      u?.display_label,
      u?.customer_name,
      u?.ops_deal_id,
    ]);
  });
}

/** Prefer board events; include requests that have not yet produced an approved event. */
export function mergeServiceLookupRows(input: {
  events: Array<{
    id: string;
    title: string;
    status: string;
    location_label?: string;
    location_address?: string;
    mfg_unit_id?: string | null;
    request_id?: string | null;
    scheduled_date?: string | null;
    quoted_amount_cents?: number | null;
    description?: string;
  }>;
  requests: Array<{
    id: string;
    status: string;
    description?: string;
    location_label?: string;
    location_address?: string;
    mfg_unit_id?: string | null;
    approved_event_id?: string | null;
    preferred_timeframe?: string;
    quoted_amount_cents?: number | null;
  }>;
}): OpsLookupServiceRow[] {
  const eventRows: OpsLookupServiceRow[] = input.events.map((e) => ({
    id: e.id,
    kind: "event",
    title: (e.title || "").trim() || "Untitled job",
    status: e.status,
    site: [e.location_label, e.location_address].filter(Boolean).join(" — ") || "—",
    mfg_unit_id: e.mfg_unit_id ?? null,
    request_id: e.request_id ?? null,
    scheduled_date: e.scheduled_date ?? null,
    quoted_amount_cents: e.quoted_amount_cents ?? null,
    description: e.description ?? "",
  }));

  const coveredRequestIds = new Set(
    eventRows.map((r) => r.request_id).filter((id): id is string => Boolean(id)),
  );
  for (const e of input.events) {
    if (e.request_id) coveredRequestIds.add(e.request_id);
  }

  const requestRows: OpsLookupServiceRow[] = [];
  for (const req of input.requests) {
    if (req.approved_event_id || coveredRequestIds.has(req.id)) continue;
    if (req.status === "cancelled" || req.status === "declined") continue;
    requestRows.push({
      id: req.id,
      kind: "request",
      title: (req.description || "").trim() || "Service request",
      status: req.status,
      site: [req.location_label, req.location_address].filter(Boolean).join(" — ") || "—",
      mfg_unit_id: req.mfg_unit_id ?? null,
      request_id: req.id,
      scheduled_date: null,
      quoted_amount_cents: req.quoted_amount_cents ?? null,
      description: req.description ?? "",
    });
  }

  return [...eventRows, ...requestRows];
}

export function servicesForUnitId(
  rows: OpsLookupServiceRow[],
  unitId: string | null | undefined,
): OpsLookupServiceRow[] {
  if (!unitId) return [];
  return rows.filter((r) => r.mfg_unit_id === unitId);
}

export function joinSaleAndUnit(
  sale: OpsLookupSaleRow | null,
  unit: OpsLookupUnitRow | null,
): OpsLookupJoined {
  return { sale, unit };
}
